import React from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";

type SupportModalProps = {
  open: boolean;
  onClose: () => void;
};

export const SupportModal: React.FC<SupportModalProps> = ({ open, onClose }) => {
  if (!open) return null;

  const handleEmailClick = () => {
    const email = "support@slashmusic.net";
    
    // Создаем mailto ссылку с subject и body для предзаполнения письма
    // mailto: протокол открывает ПОЧТОВЫЙ КЛИЕНТ ПО УМОЛЧАНИЮ, настроенный в системе
    // 
    // ВОЗМОЖНЫЕ ПРИЛОЖЕНИЯ ПО ПЛАТФОРМАМ:
    //
    // WINDOWS:
    // - Microsoft Outlook (если установлен)
    // - Windows Mail (встроенный в Windows 10/11)
    // - Mozilla Thunderbird
    // - Gmail Desktop App (если установлен)
    // - Mailbird
    // - eM Client
    //
    // macOS:
    // - Apple Mail (по умолчанию)
    // - Microsoft Outlook (если установлен)
    // - Spark
    // - Airmail
    // - Mailbox
    // - Gmail Desktop App (если установлен)
    //
    // LINUX:
    // - Thunderbird
    // - Evolution
    // - Geary
    // - KMail
    //
    // MOBILE (iOS):
    // - Apple Mail (если настроен)
    // - Gmail App (если установлен и выбран по умолчанию)
    // - Outlook Mobile (если установлен и выбран по умолчанию)
    // - Spark (если установлен и выбран по умолчанию)
    //
    // MOBILE (Android):
    // - Gmail App (обычно по умолчанию)
    // - Samsung Email (на Samsung устройствах)
    // - Outlook Mobile (если установлен)
    // - Blue Mail
    // - K-9 Mail
    //
    // ВАЖНО: Откроется то приложение, которое пользователь настроил как
    // "почтовый клиент по умолчанию" в настройках системы/браузера.
    // Если почтовый клиент не настроен, браузер может показать диалог выбора.
    const mailtoLink = `mailto:${email} `;
    
    // Попытка открыть установленный почтовый клиент
    const link = document.createElement('a');
    link.href = mailtoLink;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    try {
      link.click();
    } catch (error) {
      // Fallback на window.location.href если link.click() не работает
      window.location.href = mailtoLink;
    } finally {
      document.body.removeChild(link);
    }
  };

  const handleDiscordClick = () => {
    window.open("https://discord.gg/9D8KazSDF3", "_blank", "noopener,noreferrer");
  };

  const content = (
    <SupportOverlay onClick={onClose}>
      <SupportDialog onClick={(e) => e.stopPropagation()}>
        <SupportTitle>Need help?</SupportTitle>
        <SupportText>
          Contact us and we&apos;ll be happy to help you with any questions about Slash Music <br></br>Email: support@slashmusic.net
        </SupportText>
        <SupportButtonsRow>
          <SupportButton type="button" onClick={handleEmailClick}>
            <MailIcon />
            <span>Email</span>
          </SupportButton>
          <SupportButton type="button" onClick={handleDiscordClick}>
            <DiscordIcon />
            <span>Discord</span>
          </SupportButton>
        </SupportButtonsRow>
      </SupportDialog>
    </SupportOverlay>
  );

  return createPortal(content, document.body);
};

const DiscordIcon: React.FC = () => (
  <svg
    width="24"
    height="20"
    viewBox="100 0 30 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M121.578 3.5691C119.626 2.09206 116.539 1.84169 116.407 1.83268C116.202 1.81647 116.006 1.92455 115.922 2.10287C115.914 2.11368 115.847 2.25958 115.773 2.48654C117.063 2.69188 118.649 3.10437 120.084 3.94195C120.314 4.07525 120.384 4.35985 120.243 4.576C120.149 4.71829 119.99 4.79575 119.825 4.79575C119.737 4.79575 119.647 4.77233 119.567 4.7255C117.1 3.2863 114.02 3.21425 113.426 3.21425C112.833 3.21425 109.751 3.2863 107.286 4.7255C107.056 4.8606 106.754 4.79395 106.612 4.5778C106.468 4.35985 106.539 4.07705 106.769 3.94195C108.204 3.10617 109.789 2.69188 111.08 2.48834C111.006 2.25958 110.938 2.11548 110.933 2.10287C110.847 1.92455 110.653 1.81287 110.446 1.83268C110.314 1.84169 107.227 2.09206 105.248 3.58891C104.216 4.48773 102.149 9.74019 102.149 14.2812C102.149 14.3622 102.172 14.4397 102.215 14.5099C103.639 16.866 107.529 17.482 108.416 17.509C108.42 17.509 108.426 17.509 108.431 17.509C108.588 17.509 108.736 17.4388 108.828 17.3199L109.724 16.1599C107.305 15.5727 106.07 14.5748 105.999 14.5153C105.796 14.3478 105.777 14.056 105.957 13.8651C106.135 13.6741 106.445 13.6561 106.648 13.8236C106.677 13.8489 108.952 15.6645 113.426 15.6645C117.908 15.6645 120.183 13.8417 120.206 13.8236C120.409 13.6579 120.718 13.6741 120.898 13.8669C121.076 14.0578 121.057 14.3478 120.854 14.5153C120.783 14.5748 119.548 15.5727 117.129 16.1599L118.025 17.3199C118.117 17.4388 118.264 17.509 118.421 17.509C118.427 17.509 118.433 17.509 118.437 17.509C119.323 17.482 123.213 16.866 124.638 14.5099C124.68 14.4397 124.703 14.3622 124.703 14.2812C124.703 9.7402 122.637 4.48774 121.578 3.5691ZM110.239 12.4367C109.291 12.4367 108.523 11.6117 108.523 10.5922C108.523 9.57268 109.291 8.74771 110.239 8.74771C111.187 8.74771 111.955 9.57268 111.955 10.5922C111.955 11.6117 111.187 12.4367 110.239 12.4367ZM116.613 12.4367C115.665 12.4367 114.897 11.6117 114.897 10.5922C114.897 9.57268 115.665 8.74771 116.613 8.74771C117.561 8.74771 118.329 9.57268 118.329 10.5922C118.329 11.6117 117.561 12.4367 116.613 12.4367Z"
      fill="#ffffff"
    />
  </svg>
);

const MailIcon: React.FC = () => (
  <svg
    width="22"
    height="18"
    viewBox="0 0 22 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="1"
      y="2"
      width="20"
      height="14"
      rx="3"
      stroke="white"
      strokeWidth="1.6"
    />
    <path
      d="M3 4.5L11 9.5L19 4.5"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SupportOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const SupportDialog = styled.div`
  width: 360px;
  max-width: 90vw;
  border-radius: 20px;
  background: #ffffff;
  padding: 22px 22px 18px 22px;
  box-shadow: 0 26px 70px rgba(15, 23, 42, 0.4);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SupportTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const SupportText = styled.div`
  font-size: 13px;
  color: #4b5563;
`;

const SupportButtonsRow = styled.div`
  margin-top: 6px;
  display: flex;
  gap: 10px;
`;

const SupportButton = styled.button`
  flex: 1;
  border-radius: 999px;
  border: none;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 8px 18px rgba(248, 113, 113, 0.35);
  transition: transform 0.1s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  span {
    white-space: nowrap;
  }

  &:hover {
    transform: translateY(-0.5px);
    box-shadow: 0 10px 24px rgba(248, 113, 113, 0.45);
  }
`;


